import { useMemo } from 'react';

type Cell = {
  value: number | string | null | undefined;
  min: number;
  max: number;
  [key: string]: any;
};

const shouldBeReadOnly = (col: number, approved?: boolean): boolean => {
  return col < 3 || !!approved;
};

const outOfRange = (cell: Cell): boolean => {
  // Treat dash "-" and sentinel value -999.999 as valid acknowledgment values (not an error)
  if (
    cell.value === '-' ||
    cell.value === null ||
    cell.value === undefined ||
    cell.value === '' ||
    cell.value === -999.999
  ) {
    return false;
  }
  const numValue = Number(cell.value);
  const max = Number(cell.max);
  const min = Number(cell.min);
  // If value can't be converted to a number, don't flag as error (non-numeric strings are handled elsewhere)
  if (isNaN(numValue)) {
    return false;
  }
  return numValue < min || numValue > max;
};

// Distinct from outOfRange: -999.999 is the backend's "never touched" default for a current-log
// cell, so it counts as empty here even though outOfRange treats it as a valid acknowledgment.
export const isRequiredFieldEmpty = (value: Cell['value']): boolean => {
  return value === null || value === undefined || value === '' || value === -999.999;
};

const buildClassName = (cell, readOnly, showRequiredFieldWarnings?: boolean) => {
  let className = '';

  if (readOnly) {
    className += 'readonly-cell';
  }

  if (outOfRange(cell)) {
    className += ' error-cell';
  }

  if (!readOnly && showRequiredFieldWarnings && isRequiredFieldEmpty(cell.value)) {
    className += ' required-empty-cell';
  }

  return className;
};

export const useFormattedTableData = (
  input: Cell[][],
  col: number,
  approved?: boolean,
  showRequiredFieldWarnings?: boolean,
) => {
  return useMemo(() => {
    if (!input) return [];

    const readOnly = shouldBeReadOnly(col, approved);

    return input.map((row) =>
      row.map((cell) => ({
        ...cell,
        readOnly,
        className: buildClassName(cell, readOnly, showRequiredFieldWarnings),
      })),
    );
  }, [input, col, approved, showRequiredFieldWarnings]);
};

export const stripFormatting = (data) =>
  data.map((row) =>
    row.map(({ readOnly, className, ...cell }) => ({
      ...cell,
      value: typeof cell.value === 'string' ? cell.value.trim() : cell.value,
    })),
  );

type UpdateChillerLogFieldParams = {
  chillerLog: any;
  setChillerLog: React.Dispatch<React.SetStateAction<any>>;
  updatedData: any;
  getField?: (log: any) => any;
  setField: (log: any, value: any) => void;
  currentFieldValue?: any;
};

export const updateChillerLogFieldIfChanged = ({
  chillerLog,
  setChillerLog,
  updatedData,
  getField,
  setField,
  currentFieldValue,
}: UpdateChillerLogFieldParams) => {
  const cleanUpdatedData = stripFormatting(updatedData);
  const currentValue = currentFieldValue ?? getField?.(chillerLog);

  if (JSON.stringify(cleanUpdatedData) === JSON.stringify(currentValue)) return;

  setChillerLog((prev) => {
    const updatedLog = { ...prev };
    setField(updatedLog, cleanUpdatedData);
    return updatedLog;
  });
};
